import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { apiService } from '../services/apiService';
import { isOwnerRole, isStudioScopedRole } from '../utils/roles';
import {
  ALL_LOCATIONS,
  clearStoredLocations,
  locationKeyFor,
  setAccountKey,
  shouldDefaultToAll,
} from '../components/LocationSwitcher';

interface Admin {
  id: string;
  email: string;
  name: string;
  role: 'admin' | 'super_admin' | 'studio' | 'studio_owner' | 'brand_owner' | 'manager' | 'instructor';
  permissions: string[];
  studio?: any; // Studio data when logged in as studio
  instructorId?: string; // Set when role === 'instructor'; links to Instructor document
  /**
   * May this instructor create/edit SESSIONS? Sent by the backend on login and
   * verify-admin, resolved from their Instructor record.
   *
   * Meaningful ONLY when role === 'instructor' — the backend reports false for
   * owners and managers, who are never gated by it. Read `canWriteSessions`
   * below rather than this field, so no page has to remember that rule.
   */
  canManageSessions?: boolean;
}

// studio owner
// studio manager
// studio instructor 

interface AuthContextType {
  admin: Admin | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  login: (email: string, password: string) => Promise<void>;
  logout: () => void;
  hasPermission: (permission: string) => boolean;
  /**
   * The single question every session create/edit/delete control should ask.
   *
   * True for super admins, studio owners and managers always; for an instructor
   * only when the studio granted `canManageSessions`. The backend's
   * StudioSessionWriteGuard enforces the same rule and stays the authority —
   * this only stops the panel offering controls that are guaranteed to 403.
   */
  canWriteSessions: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [admin, setAdmin] = useState<Admin | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    checkAuthStatus();
  }, []);

  const checkAuthStatus = async () => {
    try {
      const token = localStorage.getItem('admin_token');
      const storedAdminData = localStorage.getItem('admin_data');
      
      if (token) {
        // First try to use stored admin data
        if (storedAdminData) {
          try {
            const parsedAdmin = JSON.parse(storedAdminData);
            setAdmin(parsedAdmin);
            setIsLoading(false);
            
            // Then verify with backend in the background
            try {
              const response = await apiService.get('/api/v1/auth/verify-admin');
              const adminData = response.data || response;
              const transformedAdmin = {
                id: adminData._id,
                email: adminData.email,
                name: adminData.name || adminData.studio?.studioName,
                role: adminData.role || 'studio',
                permissions: adminData.permissions || ['studio.*'],
                studio: adminData.studio,
                instructorId: adminData.instructorId,
                canManageSessions: adminData.canManageSessions,
              };
              localStorage.setItem('admin_data', JSON.stringify(transformedAdmin));
              setAdmin(transformedAdmin);
            } catch (verifyError) {
              // If verification fails, keep using stored data
              console.log('Token verification failed, using stored admin data', verifyError);
            }
            return;
          } catch (parseError) {
            // If stored data is invalid, continue to verification
            console.log('Invalid stored admin data, verifying with backend');
          }
        }
        
        // Try to verify token with backend
        try {
          const response = await apiService.get('/api/v1/auth/verify-admin');
          const adminData = response.data || response;
          const transformedAdmin = {
            id: adminData._id,
            email: adminData.email,
            name: adminData.name || adminData.studio?.studioName,
            role: adminData.role || 'studio',
            permissions: adminData.permissions || ['studio.*'],
            studio: adminData.studio,
            instructorId: adminData.instructorId,
            canManageSessions: adminData.canManageSessions,
          };
          localStorage.setItem('admin_data', JSON.stringify(transformedAdmin));
          setAdmin(transformedAdmin);
        } catch (verifyError) {
          // If verify-admin endpoint is not available, clear auth
          console.log('Token verification failed', verifyError);
          localStorage.removeItem('admin_token');
          localStorage.removeItem('admin_data');
          setAdmin(null);
        }
      }
    } catch (error) {
      localStorage.removeItem('admin_token');
      localStorage.removeItem('admin_data');
    } finally {
      setIsLoading(false);
    }
  };

  const login = async (email: string, password: string) => {
    try {
      const response = await apiService.login({ email, password });
      
      // Check if response has the expected structure
      if (!response || !response.data) {
        throw new Error('Invalid response structure: missing data');
      }
      
      // Extract token and admin data from response
      const { token, admin: adminData } = response.data;
      
      if (!token || !adminData) {
        throw new Error('Invalid response structure: missing token or admin data');
      }
      
      // Transform admin data to match frontend interface
      const transformedAdmin = {
        id: adminData._id,
        email: adminData.email,
        name: adminData.name || adminData.studio?.studioName,
        role: adminData.role || 'studio',
        permissions: adminData.permissions || ['studio.*'],
        studio: adminData.studio,
        instructorId: adminData.instructorId,
        canManageSessions: adminData.canManageSessions,
      };
      
      // Signing in is a fresh scope. Anything left over from a previous
      // account would be sent as `x-studio-id` and refused on every request.
      clearStoredLocations();
      localStorage.setItem('admin_token', token);
      localStorage.setItem('admin_data', JSON.stringify(transformedAdmin));
      // Captured once, from the LOGIN response — before any location has been
      // chosen, so it cannot be the id of a location. Everything per-account
      // is keyed on this.
      setAccountKey(transformedAdmin.email || transformedAdmin.id);
      // ── START ON "ALL LOCATIONS" — BUT ONLY IF THERE IS MORE THAN ONE ─────
      // The stored choice is empty right after login, which sends no
      // `x-studio-id` and so shows the home location only — while the switcher
      // reads "All locations". Record `__all__` explicitly for someone who
      // holds several. A single-location owner must NOT get it: the server
      // refuses every write under `__all__`.
      if (isStudioScopedRole(transformedAdmin.role)) {
        try {
          const mine: any = await apiService.getMyBrand();
          // Two or more locations where this person is manager or above
          // (just "more than one" when the server omits roleByStudio).
          if (shouldDefaultToAll(mine)) {
            localStorage.setItem(
              locationKeyFor(transformedAdmin.id),
              ALL_LOCATIONS,
            );
          }
        } catch {
          /* no brand / older backend — no switcher, nothing to record */
        }
      }
      setAdmin(transformedAdmin);
    } catch (error: any) {
      console.error('Login error:', error);
      throw error;
    }
  };

  const logout = () => {
    localStorage.removeItem('admin_token');
    localStorage.removeItem('admin_data');
    // A chosen location belongs to the session that chose it.
    clearStoredLocations();
    setAdmin(null);
    apiService.logout().catch(() => {
      // Ignore logout errors
    });
  };

  const hasPermission = (permission: string): boolean => {
    if (!admin) {
      console.log('hasPermission: No admin found');
      return false;
    }
    if (admin.role === 'super_admin') {
      return true;
    }
    if (isOwnerRole(admin.role)) {
      // Studios have access to their own data only
      // Map studio permissions to page permissions
      const studioAllowedPermissions = [
        'classes.view',
        'events.view',
        'bookings.view',
        'transactions.view',
        'packs.view',
        'promo_codes.view',
        // Needed for /studios/:id — that route IS a studio owner's own
        // "Studio Profile" page (/studio-profile redirects to it). Access to
        // OTHER studios' ids, and to the /studios directory list, is blocked in
        // AdminGuard (restrictStudioRoleToOwnParam / blockStudioRoles), not here.
        'studios.view',
        'instructors.view',
        'studio.*',
        'advertisements.view',
        'users.view', // studio owners see the Users page scoped to their own members
      ];
      const hasAccess = permission.startsWith('studio.') ||
             admin.permissions.includes(permission) ||
             studioAllowedPermissions.includes(permission);
      console.log('hasPermission for studio:', { permission, hasAccess, role: admin.role, permissions: admin.permissions });
      return hasAccess;
    }
    if (admin.role === 'manager') {
      const managerPermissions = [
        'classes.view',
        'classes.create',
        'classes.edit',
        'events.view',
        'events.create',
        'events.edit',
        'bookings.view',
        'bookings.edit',
        'packs.view',
        'packs.create',
        'packs.edit',
        'packs.delete',
        'promo_codes.view',
        // See the studio-role list above — needed for the own-studio profile
        // page; cross-studio access is blocked in AdminGuard.
        'studios.view',
        'instructors.view',
        'studio.*',
        'advertisements.view',
        'users.view', // managers see the Users page scoped to their studio's members
      ];
      return (
        permission.startsWith('studio.') ||
        managerPermissions.includes(permission) ||
        admin.permissions.includes(permission)
      );
    }
    if (admin.role === 'instructor') {
      const instructorViewAllBookings =
        (admin.studio as any)?.whitelabelConfig?.instructorViewAllBookings === true;
      if (instructorViewAllBookings) {
        const instructorAllPermissions = ['classes.view', 'bookings.view', 'sessions.view'];
        if (instructorAllPermissions.includes(permission)) return true;
      }
      return permission === 'classes.view' || admin.permissions.includes(permission);
    }
    return admin.permissions.includes(permission);
  };

  /**
   * `!== false`, not `=== true` — the same reading the backend guard uses.
   *
   * The Instructor schema stamps its `true` default onto NEW documents only, so
   * every record predating the field carries no value at all. Treating absent
   * as restricted would hide the schedule controls from an entire existing
   * roster the API would have let through.
   *
   * An admin whose session predates this field in localStorage also lands here
   * as undefined, and reads as permitted rather than silently locked out.
   */
  const canWriteSessions =
    !admin ? false
    : admin.role !== 'instructor' ? true
    : admin.canManageSessions !== false;

  const value: AuthContextType = {
    admin,
    isAuthenticated: !!admin,
    isLoading,
    login,
    logout,
    hasPermission,
    canWriteSessions,
  };

  // Debug log
  React.useEffect(() => {
    if (admin) {
      console.log('AuthContext - Admin set:', { id: admin.id, email: admin.email, role: admin.role, permissions: admin.permissions });
    } else {
      console.log('AuthContext - No admin');
    }
  }, [admin]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
